import React from 'react';
import { createRoot } from 'react-dom/client';
import { create } from 'mobx-persist';
import { ApolloProvider } from '@apollo/client';
import { IntlProvider } from 'react-intl';
import { ThemeProvider } from 'styled-components';
import registerServiceWorker from './registerServiceWorker';
import FileUploadStore from './containers/analyse/store/FileUploadStore';
import UiState from './store/UiState';
import RoutingStore from './store/RoutingStore';
import theme from './theme';
import GlobalStyle from './theme/global-styles';
import { StoresProvider } from './store/StoresContext';
import './store/serializrSchemas';

// eslint-disable-next-line import/extensions
import './theme/global-styles.js';

// import views
import App from './App';

// Import i18n messages
import { translationMessages } from './i18n';
import AuthStore from './containers/login/store/AuthStore';
import { initialiseAPI } from './services/api';
import { appSyncClient, setAWSAppSyncClient } from './services/aws/app-sync';

// setup all stores
const uiState = new UiState();
const routing = new RoutingStore();
const authStore = new AuthStore(uiState);
const fileUploadStore = new FileUploadStore(uiState);

const stores = {
  routing,
  fileUploadStore,
  uiState,
  authStore
};

// Create a single React root for the lifetime of the app
const rootElement = document.getElementById('root');
const root = rootElement ? createRoot(rootElement) : null;

const render = () => {
  if (!root) return;

  root.render(
    <ApolloProvider client={appSyncClient}>
      <StoresProvider value={stores}>
        <IntlProvider locale="en" messages={translationMessages.en}>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            <App />
          </ThemeProvider>
        </IntlProvider>
      </StoresProvider>
    </ApolloProvider>
  );
};

/**
 * responsible for persistance of stores every store we want te keep alive between refreshes
 * we have to wait for the persiststore finish before we can render our views
 */
const persistStores = async () => {
  const hydrate = create();
  await hydrate('uiState', stores.uiState);
  await hydrate('authStore', stores.authStore);

  await initialiseAPI();

  // Ensure Apollo client exists before first render.
  await setAWSAppSyncClient();

  authStore.setLoginState();

  render();
};

registerServiceWorker();
persistStores();
